import { useEffect, useMemo, useRef } from "react";
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  Clock,
  FileText,
  Lock,
  Loader2,
} from "lucide-react";
import { CaptureActivityPanel } from "@/components/spoken-memory/CaptureActivityPanel";
import {
  feedDateLabel,
  formatTimeOfDay,
  useSessionsFeed,
  type AmbientBacklogFeedSummary,
  type SessionFeedRow,
} from "@/data/session-feed";

interface HomeScreenProps {
  workspaceSlug: string;
  onOpenSession: (source: "capture" | "capture_pending", id: string) => void;
  highlightedSessionId?: string | null;
}

export function HomeScreen({
  workspaceSlug,
  onOpenSession,
  highlightedSessionId = null,
}: HomeScreenProps) {
  const { rows, captureActivity, ambientBacklog } =
    useSessionsFeed(workspaceSlug);

  const grouped = useMemo(() => groupByDay(rows), [rows]);

  return (
    <div className="mx-auto flex min-h-full max-w-[880px] flex-col p-9">
      {captureActivity.length > 0 ? (
        <div className="mb-9">
          <CaptureActivityPanel
            activities={captureActivity}
            showDiagnostics={false}
          />
        </div>
      ) : null}

      {ambientBacklog ? (
        <div className="mb-8">
          <AmbientBacklogNotice backlog={ambientBacklog} />
        </div>
      ) : null}

      <div className="mb-9">
        {grouped.length === 0 ? (
          <div className="px-3 py-6 text-[13px] text-muted-foreground">
            No captures yet. Device captures will show up here.
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.key}>
              <DateLabel>{group.label}</DateLabel>
              {group.rows.map((row) => (
                <SessionListRow
                  key={`${row.source}_${row.id}`}
                  row={row}
                  highlighted={row.openable && row.id === highlightedSessionId}
                  onClick={() => {
                    if (row.openable) onOpenSession(row.source, row.id);
                  }}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AmbientBacklogNotice({
  backlog,
}: {
  backlog: AmbientBacklogFeedSummary;
}) {
  const latest = backlog.newestStartedAt
    ? formatTimeOfDay(backlog.newestStartedAt)
    : null;
  const statusParts = [
    `${backlog.count} ambient capture${backlog.count === 1 ? "" : "s"}`,
    backlog.processingCount > 0
      ? `${backlog.processingCount} processing`
      : null,
    backlog.retryingCount > 0 ? `${backlog.retryingCount} retrying` : null,
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-border/70 bg-card/35 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-foreground">
            Ambient audio pending
          </div>
          <div className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
            Catching up in the background. Manual recordings still process
            first.
          </div>
        </div>
        <div className="shrink-0 text-right text-[12px] text-muted-foreground">
          <div>{statusParts.join(" · ")}</div>
          {latest ? <div className="mt-0.5">Latest {latest}</div> : null}
        </div>
      </div>
    </div>
  );
}

function DateLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 mb-1.5 px-1 text-[12px] text-muted-foreground">
      {children}
    </div>
  );
}

function SessionListRow({
  row,
  highlighted,
  onClick,
}: {
  row: SessionFeedRow;
  highlighted: boolean;
  onClick: () => void;
}) {
  const rowRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!highlighted) return;
    rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const Icon =
    row.iconKind === "locked"
      ? Lock
      : row.iconKind === "captured"
        ? CheckCircle2
        : row.iconKind === "stale"
          ? AlertTriangle
          : row.iconKind === "queued"
            ? Clock
            : row.iconKind === "processing"
              ? Loader2
              : Briefcase;
  return (
    <button
      ref={rowRef}
      type="button"
      data-session-list-row="true"
      onClick={onClick}
      disabled={!row.openable}
      aria-current={highlighted ? "true" : undefined}
      className={`grid w-full grid-cols-[28px_1fr_auto_auto_72px] items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors ${
        highlighted
          ? "cursor-pointer bg-card ring-1 ring-border"
          : row.openable
            ? "cursor-pointer hover:bg-card"
            : "cursor-default"
      }`}
    >
      <div className="grid h-7 w-7 place-items-center rounded-md border border-border bg-background text-muted-foreground">
        <FileText className="h-3.5 w-3.5" />
      </div>
      <div className="flex min-w-0 flex-col">
        <div className="truncate text-[14px] text-foreground">{row.title}</div>
        <div className="text-[12px] text-muted-foreground">
          {row.subtitle ??
            (row.source === "capture_pending"
              ? "Preparing capture"
              : "Capture")}
        </div>
      </div>
      <span className={`proto-pill pill-${row.pillKind}`}>
        {row.pillLabel ?? row.pillKind}
      </span>
      <Icon
        className={`h-3.5 w-3.5 text-muted-foreground ${row.iconKind === "processing" ? "animate-spin" : ""}`}
      />
      <div className="text-right font-mono text-[13px] text-muted-foreground">
        {formatTimeOfDay(row.startedAt)}
      </div>
    </button>
  );
}

function groupByDay(
  rows: SessionFeedRow[],
): Array<{ key: string; label: string; rows: SessionFeedRow[] }> {
  const now = Date.now();
  const groups = new Map<string, SessionFeedRow[]>();
  for (const row of rows) {
    const label = feedDateLabel(row.startedAt, now);
    const bucket = groups.get(label);
    if (bucket) bucket.push(row);
    else groups.set(label, [row]);
  }
  return Array.from(groups.entries()).map(([label, rows]) => ({
    key: label,
    label,
    rows,
  }));
}
